import Ajv from "ajv";
import type { AnySchema, ErrorObject } from "ajv";
import Papa from "papaparse";
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";

export type DataFormat = "json" | "csv" | "yaml";
export type FormatChoice = DataFormat | "auto";
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type RowRecord = Record<string, JsonValue>;

export interface FieldProfile {
  name: string;
  observed: number;
  missing: number;
  nullish: number;
  blank: number;
  typeCounts: Record<string, number>;
  samples: string[];
  required: boolean;
}

export interface QualityIssue {
  severity: "warning" | "error";
  title: string;
  detail: string;
  count?: number;
}

export interface DatasetStats {
  rowCount: number;
  columnCount: number;
  sourceBytes: number;
  validRatio: number;
  parseMs: number;
}

export interface ParsedDataset {
  detectedFormat: DataFormat;
  requestedFormat: FormatChoice;
  source: JsonValue;
  rows: RowRecord[];
  columns: string[];
  fields: FieldProfile[];
  issues: QualityIssue[];
  parseErrors: string[];
  schema: JsonValue;
  schemaValid: boolean;
  schemaErrors: string[];
  stats: DatasetStats;
}

const ajv = new Ajv({ allErrors: true, strict: false });

export function detectFormat(text: string, fileName?: string): DataFormat {
  const lowerName = fileName?.toLowerCase() ?? "";
  if (lowerName.endsWith(".csv") || lowerName.endsWith(".tsv")) return "csv";
  if (lowerName.endsWith(".yaml") || lowerName.endsWith(".yml")) return "yaml";
  if (lowerName.endsWith(".json")) return "json";

  const trimmed = text.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return "json";
  const firstLine = trimmed.split(/\r?\n/, 1)[0] ?? "";
  if (firstLine.includes(",") || firstLine.includes("\t")) return "csv";
  return "yaml";
}

export function parseDataset(text: string, requestedFormat: FormatChoice, fileName?: string): ParsedDataset {
  const start = performance.now();
  const detectedFormat = requestedFormat === "auto" ? detectFormat(text, fileName) : requestedFormat;
  const parseErrors: string[] = [];
  const issues: QualityIssue[] = [];
  let source: JsonValue;
  let rows: RowRecord[];

  try {
    if (detectedFormat === "json") {
      source = toJsonValue(JSON.parse(text));
      rows = normalizeRows(source);
    } else if (detectedFormat === "yaml") {
      source = toJsonValue(parseYaml(text));
      rows = normalizeRows(source);
    } else {
      const duplicateHeaders = detectDuplicateHeaders(text);
      duplicateHeaders.forEach((header) => {
        issues.push({
          severity: "warning",
          title: "Duplicate CSV header",
          detail: `The header "${header}" appears more than once. Columns may be renamed by the parser.`
        });
      });
      const result = Papa.parse<Record<string, unknown>>(text, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true
      });
      result.errors.forEach((error) => {
        parseErrors.push(`${error.code}: ${error.message}${typeof error.row === "number" ? ` at row ${error.row + 1}` : ""}`);
      });
      rows = result.data.map((row) => normalizeUnknownRecord(row));
      source = rows;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown parse error";
    parseErrors.push(message);
    source = [];
    rows = [];
  }

  const columns = collectColumns(rows);
  const fields = inferFields(rows, columns);
  const schema = buildSchemaFromFields(fields);
  const schemaResult = validateWithSchema(source, schema);
  const fieldIssues = buildQualityIssues(fields, rows.length);
  const allIssues = [
    ...issues,
    ...fieldIssues,
    ...parseErrors.map((message) => ({
      severity: "error" as const,
      title: "Parse error",
      detail: message
    }))
  ];
  const totalCells = Math.max(rows.length * Math.max(columns.length, 1), 1);
  const flaggedCells = fields.reduce((sum, field) => sum + field.blank + field.nullish, 0);
  const validRatio = Math.max(0, Math.round((1 - flaggedCells / totalCells) * 1000) / 10);

  return {
    detectedFormat,
    requestedFormat,
    source,
    rows,
    columns,
    fields,
    issues: allIssues,
    parseErrors,
    schema,
    schemaValid: schemaResult.valid,
    schemaErrors: schemaResult.errors,
    stats: {
      rowCount: rows.length,
      columnCount: columns.length,
      sourceBytes: new Blob([text]).size,
      validRatio,
      parseMs: Math.max(1, Math.round(performance.now() - start))
    }
  };
}

export function buildOutput(dataset: ParsedDataset, format: DataFormat): string {
  if (format === "json") return JSON.stringify(dataset.source, null, 2);
  if (format === "yaml") return stringifyYaml(dataset.source);
  if (dataset.rows.length === 0) return "";
  const csvRows = dataset.rows.map((row) => {
    const normalized: Record<string, string> = {};
    dataset.columns.forEach((column) => {
      normalized[column] = stringifyCell(row[column] ?? null);
    });
    return normalized;
  });
  return Papa.unparse(csvRows, { columns: dataset.columns });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function stringifyCell(value: JsonValue): string {
  if (value === null) return "";
  if (Array.isArray(value) || isJsonRecord(value)) return JSON.stringify(value);
  return String(value);
}

export function typeName(value: JsonValue | undefined): string {
  if (value === undefined) return "missing";
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (isJsonRecord(value)) return "object";
  if (typeof value === "number" && Number.isInteger(value)) return "integer";
  return typeof value;
}

export function isJsonRecord(value: JsonValue): value is Record<string, JsonValue> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toJsonValue(value: unknown): JsonValue {
  if (value === null) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((entry) => toJsonValue(entry));
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return Object.fromEntries(Object.entries(record).map(([key, entry]) => [key, toJsonValue(entry)]));
  }
  if (typeof value === "bigint") return value.toString();
  return String(value);
}

function normalizeUnknownRecord(row: Record<string, unknown>): RowRecord {
  const normalized: RowRecord = {};
  Object.entries(row).forEach(([key, value]) => {
    normalized[key] = toJsonValue(value);
  });
  return normalized;
}

function normalizeRows(source: JsonValue): RowRecord[] {
  if (Array.isArray(source)) {
    if (source.every((entry) => isJsonRecord(entry))) {
      return source.map((entry) => flattenRecord(entry));
    }
    return source.map((entry, index) => ({ index, value: entry }));
  }

  if (isJsonRecord(source)) {
    const nestedArray = findRecordArray(source);
    if (nestedArray.length > 0) return nestedArray.map((entry) => flattenRecord(entry));
    return [flattenRecord(source)];
  }

  return [{ value: source }];
}

function findRecordArray(record: Record<string, JsonValue>): Record<string, JsonValue>[] {
  for (const value of Object.values(record)) {
    if (Array.isArray(value) && value.every((entry) => isJsonRecord(entry))) return value;
    if (isJsonRecord(value)) {
      const nested = findRecordArray(value);
      if (nested.length > 0) return nested;
    }
  }
  return [];
}

function flattenRecord(record: Record<string, JsonValue>, prefix = ""): RowRecord {
  const flattened: RowRecord = {};
  Object.entries(record).forEach(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (isJsonRecord(value)) {
      Object.assign(flattened, flattenRecord(value, nextKey));
    } else {
      flattened[nextKey] = value;
    }
  });
  return flattened;
}

function collectColumns(rows: RowRecord[]): string[] {
  const ordered = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => ordered.add(key));
  });
  return Array.from(ordered);
}

function inferFields(rows: RowRecord[], columns: string[]): FieldProfile[] {
  return columns.map((column) => {
    const typeCounts: Record<string, number> = {};
    const samples: string[] = [];
    let observed = 0;
    let nullish = 0;
    let blank = 0;

    rows.forEach((row) => {
      const value = row[column];
      if (value === undefined) return;
      observed += 1;
      const type = typeName(value);
      typeCounts[type] = (typeCounts[type] ?? 0) + 1;
      if (value === null) nullish += 1;
      if (value === "") blank += 1;
      const sample = stringifyCell(value);
      if (sample !== "" && samples.length < 4 && !samples.includes(sample)) samples.push(sample);
    });

    const missing = Math.max(rows.length - observed, 0);
    return {
      name: column,
      observed,
      missing,
      nullish,
      blank,
      typeCounts,
      samples,
      required: missing === 0 && nullish === 0 && blank === 0
    };
  });
}

function buildSchemaFromFields(fields: FieldProfile[]): JsonValue {
  const properties: Record<string, JsonValue> = {};
  const required: JsonValue[] = [];

  fields.forEach((field) => {
    properties[field.name] = {
      type: schemaTypes(field),
      examples: field.samples.slice(0, 2)
    };
    if (field.required) required.push(field.name);
  });

  return {
    type: "array",
    items: {
      type: "object",
      additionalProperties: true,
      required,
      properties
    }
  };
}

function schemaTypes(field: FieldProfile): JsonValue {
  const mapped = Object.keys(field.typeCounts).map((type) => {
    if (type === "integer") return "integer";
    if (type === "array") return "array";
    if (type === "object") return "object";
    if (type === "boolean") return "boolean";
    if (type === "number") return "number";
    if (type === "null") return "null";
    return "string";
  });
  const unique = Array.from(new Set(mapped));
  if (unique.length === 0) return "string";
  if (unique.length === 1) return unique[0] ?? "string";
  return unique;
}

function validateWithSchema(source: JsonValue, schema: JsonValue): { valid: boolean; errors: string[] } {
  const sourceForSchema = Array.isArray(source) ? normalizeRows(source) : normalizeRows(source);
  const validate = ajv.compile(schema as AnySchema);
  const validationResult = validate(sourceForSchema);
  const valid = validationResult === true;
  const errors = (validate.errors ?? []).map((error: ErrorObject) => {
    const path = error.instancePath || "/";
    return `${path} ${error.message ?? "is invalid"}`;
  });
  return { valid, errors };
}

function buildQualityIssues(fields: FieldProfile[], rowCount: number): QualityIssue[] {
  const issues: QualityIssue[] = [];
  fields.forEach((field) => {
    const typeCount = Object.keys(field.typeCounts).filter((type) => type !== "null").length;
    if (field.missing + field.nullish + field.blank > 0) {
      issues.push({
        severity: "warning",
        title: `Missing values in "${field.name}"`,
        detail: `${field.missing + field.nullish + field.blank} of ${rowCount} rows are missing, blank, or null.`,
        count: field.missing + field.nullish + field.blank
      });
    }
    if (typeCount > 1) {
      issues.push({
        severity: "warning",
        title: `Mixed types in "${field.name}"`,
        detail: `Observed ${Object.entries(field.typeCounts)
          .map(([type, count]) => `${type}:${count}`)
          .join(", ")}.`,
        count: typeCount
      });
    }
  });
  return issues;
}

function detectDuplicateHeaders(text: string): string[] {
  const firstLine = text.trim().split(/\r?\n/, 1)[0] ?? "";
  if (!firstLine) return [];
  const parsed = Papa.parse<string[]>(firstLine, { header: false });
  const firstRow = parsed.data[0] ?? [];
  const counts = new Map<string, number>();
  firstRow.forEach((header) => {
    const normalized = header.trim();
    if (!normalized) return;
    counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .filter(([, count]) => count > 1)
    .map(([header]) => header);
}
